import React, { useEffect, useState } from 'react';
import { Typography, Row, Col, Form, Input, Button, Divider, message, Carousel, Avatar } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RightOutlined } from '@ant-design/icons';
import { USER_URL, BUSINESS_URL } from 'constants/ApiConstants';
import { setAuth, isLoggedIn } from 'utils/auth';
import request from 'utils/request';
import { validateEmail, validatePhoneNumber, validatePassword } from './validation';
import FooterLinks from 'components/UtilComponents/FooterLinks';
import { posthog } from 'posthog-js';
import { getBreakPoint } from 'utils/layout';
import { FaStar } from 'react-icons/fa';
import BuyofuelLogo from 'assets/images/logo-white-bg.png';
import { onOpenAppStore } from '../utils/browser';

const { Title, Text, Link, Paragraph } = Typography;

// ─── Main Login Component ─────────────────────────────────────────────────────

const Login = () => {
  const navigate = useNavigate();
  const isMobile = !getBreakPoint().includes('lg');
  const [form] = Form.useForm();
  const [loginType, setLoginType] = useState('password');
  const [counter, setCounter] = useState(0);
  const [step, setStep] = useState('send-otp');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isLoggedIn()) checkSignUp();
  }, []);

  const checkSignUp = async () => {
    const businessRes = await request(`${BUSINESS_URL}/me?populate=true`, { method: 'get' });
    if (!businessRes.business?.businessType) {
      window.location = '/signup';
    } else {
      const url = localStorage.getItem('copiedLink');
      if (url) {
        window.location = url;
        localStorage.removeItem('copiedLink');
      } else {
        window.location = '/';
      }
    }
  };

  useEffect(() => {
    setStep('send-otp');
    setCounter(0);
  }, [loginType]);

  useEffect(() => {
    const timer = counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
    return () => clearInterval(timer);
  }, [counter]);

  // ── After login: decide whether to show the picker or go straight through ──
  const handlePostLogin = async (authData, businesses) => {
    // const { _id, token, aclGroup } = authData;

    if (businesses.length === 1) {
      // Single business — call select-entity silently and navigate home
      await selectEntity(authData, businesses[0]);
    } else {
      selectEntity(authData, businesses[0]);
    }
  };

  // ── Call /select-entity, then navigate home ────────────────────────────────
  const selectEntity = async (authData, business) => {
    try {
      const res = await request(`${USER_URL}/select-entity`, {
        method: 'post',
        // The session token set by setAuth is already in request headers;
        // if your `request` util reads from localStorage you may need to
        // call setAuth before this. See note below.
        body: JSON.stringify({ businessId: business._id }),
      });

      if (res.status === 'error') {
        message.error(res.error || 'Failed to select business. Please try again.');
        return;
      }

      // Persist auth now that the session's activeEntityId is set server-side
      await setAuth({
        _id: authData._id,
        token: authData.token,
        aclGroup: authData.aclGroup,
        expiryTimeStamp: authData.expiryTimeStamp,
        validity: authData.validity,
      });

      posthog.capture('$login', {
        login_type: loginType === 'password' ? 'email-password' : 'email-otp',
        $set: { lastLoginDate: new Date().toISOString() },
      });

      const url = localStorage.getItem('copiedLink');
      if (url) {
        localStorage.removeItem('copiedLink');
        window.location = url;
      } else {
        window.location = '/';
      }
    } catch (err) {
      console.error('[select-entity]', err);
      message.error(err.message || 'An unexpected error occurred. Please try again.');
    }
  };

  // ── Password login ─────────────────────────────────────────────────────────
  const onSubmit = (values) => {
    if (loginType === 'password') {
      onValidatePassword(values);
    } else if (step === 'send-otp') {
      onSendOTP(values);
    } else {
      onVerifyOTP(values);
    }
  };

  const onValidatePassword = async (values) => {
    setLoading(true);
    try {
      const res = await axios.post(`${USER_URL}/login`, {
        username: values.username,
        password: values.password,
        platform: 'Web',
      });

      if (res.data.status === 'success') {
        const { _id, token, aclGroup, businesses = [], expiryTimeStamp, validity } = res.data;

        if (aclGroup !== 'business') {
          return message.error('Only business users can login to the portal');
        }

        // Set token immediately so select-entity request is authenticated
        await setAuth({ _id, token, aclGroup, expiryTimeStamp, validity });

        await handlePostLogin({ _id, token, aclGroup, expiryTimeStamp, validity }, businesses);
      } else {
        message.error(res.data.error);
      }
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── OTP flow ───────────────────────────────────────────────────────────────
  const onSendOTP = async () => {
    setLoading(true);
    try {
      const res = await request(`${USER_URL}/send/otp`, {
        method: 'post',
        body: JSON.stringify({ username: form.getFieldValue('username') }),
      });
      if (res.status === 'error') {
        if (res.code === 1006) message.error('Account does not exist');
        else console.log({ username: res.error });
      } else {
        message.success('OTP sent successfully to your Email Address and Phone Number');
        setStep('verify-otp');
        setCounter(59);
      }
      setTimeout(() => setLoading(false), 3000);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const onVerifyOTP = async (values) => {
    setLoading(true);
    try {
      const res = await request(`${USER_URL}/login`, {
        method: 'post',
        body: JSON.stringify({
          username: values?.username,
          otp: values?.otp,
          platform: 'Web',
        }),
      });

      if (res.status === 'error') {
        if (res.code === 1007) message.error('Please provide a valid OTP');
        else if (res.code === 1012) message.error('Account has been deactivated');
        else console.log({ otp: res.error });
      } else {
        if (res?.aclGroup !== 'business') {
          return message.error('Only business users can login to the portal');
        }

        const authData = {
          token: res.token,
          expiryTimeStamp: res.expiryTimeStamp,
          validity: res.validity,
          aclGroup: res.aclGroup,
          _id: res._id,
        };
        const businesses = res.businesses ?? [];

        // Set token so select-entity request is authenticated
        await setAuth(authData);

        await handlePostLogin(authData, businesses);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Row align="middle" justify="space-around" className="vh-100">
        {isMobile ? (
          <Col span={24}>
            <Row align="center" className="px-2" style={{ minHeight: '100px', backgroundColor: '#e8f2fc' }}>
              <Col className="align-content-center text-start" span={3}>
                <Avatar src={BuyofuelLogo} shape="square" style={{ height: '50px', width: '50px' }} />
              </Col>
              <Col className="mx-1" xs={11} sm={14} md={14} lg={14}>
                <div className="mx-2 my-4">
                  <Text style={{ fontSize: 'calc(9px + 0.75vw)' }}>Upgrade Your Experience</Text>
                  <div>
                    <span style={{ fontSize: 'calc(10px + 0.31vw)' }}>
                      <FaStar size={'calc(10px + 0.31vw)'} color="#FFDF00" /> 4/5 <Text type="secondary"> | 10T+ Downloads</Text>
                    </span>
                  </div>
                </div>
              </Col>
              <Col className="align-content-center text-right" xs={9} sm={4} md={4} lg={4}>
                <Button style={{ height: '40px' }} type="primary" onClick={onOpenAppStore}>
                  Open in App
                </Button>
              </Col>
            </Row>
          </Col>
        ) : (
          <Col xs={24} sm={24} md={24} lg={12}>
            <Carousel arrows={true} autoplay pauseOnHover={false}>
              <img src={require('assets/images/slides/slide-1.jpg')} alt="Banner 1" />
              <img src={require('assets/images/slides/slide-2.jpg')} alt="Banner 2" />
              <img src={require('assets/images/slides/slide-3.jpg')} alt="Banner 3" />
              <img src={require('assets/images/slides/slide-4.jpg')} alt="Banner 4" />
            </Carousel>
          </Col>
        )}

        <Col xs={24} sm={24} md={24} lg={12} className="p-3 p-sm-5">
          <Title level={3} className="m-0 fw-bold">
            Login
          </Title>
          <Paragraph>Login with {loginType === 'password' ? 'Password' : 'OTP'}</Paragraph>

          <Form className="mt-5" form={form} layout="vertical" onFinish={onSubmit}>
            <Form.Item
              className="bg-transparent"
              name="username"
              rules={[
                { required: true, message: 'Please enter your Phone Number / Email Address' },
                () => ({
                  validator(_, value) {
                    if (!value || validateEmail(value) || validatePhoneNumber(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject('Please enter a valid email or a 10-digit Phone Number');
                  },
                }),
              ]}
              hasFeedback>
              <Input size="large" placeholder="Phone Number / Email Address" allowClear />
            </Form.Item>

            {loginType === 'password' && (
              <>
                <Form.Item className="bg-transparent" name="password" rules={[{ required: true, message: 'Please enter your password' }, { validator: validatePassword }]} hasFeedback>
                  <Input.Password
                    size="large"
                    placeholder="Password"
                    allowClear
                    iconRender={(visible) => (
                      <Text underline style={{ cursor: 'pointer' }}>
                        {visible ? 'Hide' : 'Show'}
                      </Text>
                    )}
                  />
                </Form.Item>
                <Form.Item className="mt-0">
                  <Link
                    onClick={() => {
                      posthog.capture(`$screenview | $forgot-password`, {
                        $set: { lastActivityDate: new Date().toISOString() },
                      });
                      navigate('/forgot-password');
                    }}
                    underline>
                    Forgot Password?
                  </Link>
                </Form.Item>
              </>
            )}

            {loginType === 'otp' && step === 'verify-otp' && (
              <Form.Item
                className="bg-transparent"
                name="otp"
                rules={[
                  { required: true, message: 'Please enter the OTP received' },
                  { message: 'Please enter a valid OTP', pattern: new RegExp('^[0-9]{6}$') },
                ]}
                hasFeedback
                extra={
                  <Row className="mt-2" align="middle">
                    <Text>Didn't receive OTP? {counter > 0 && `Request again in ${counter}s`}</Text>
                    {counter === 0 && (
                      <Button type="link" size="middle" className="px-2" onClick={onSendOTP}>
                        Re-send OTP
                      </Button>
                    )}
                  </Row>
                }>
                <Input size="large" placeholder="Enter 6 Digit OTP" allowClear />
              </Form.Item>
            )}

            <Form.Item>
              <Row justify="center" className="my-2">
                <Button type="primary" htmlType="submit" size="large" style={{ width: '250px' }} loading={loading}>
                  {loginType === 'password' && 'Login'}
                  {loginType === 'otp' && step === 'send-otp' && 'Request OTP'}
                  {loginType === 'otp' && step === 'verify-otp' && 'Verify OTP'}
                </Button>
              </Row>
              <Row justify="center" className="my-2">
                <Button
                  onClick={() => {
                    setLoginType(loginType === 'password' ? 'otp' : 'password');
                    form.resetFields();
                  }}
                  type="link"
                  size="large"
                  style={{ width: '250px' }}>
                  Login with {loginType === 'password' ? 'OTP' : 'password'} instead <RightOutlined />
                </Button>
              </Row>
            </Form.Item>

            <Row align="middle" justify="center" className="mb-5">
              <Text>
                Don't have an account?{' '}
                <Link
                  onClick={() => {
                    posthog.capture(`$screenview | $signup`, {
                      $set: { lastActivityDate: new Date().toISOString() },
                    });
                    navigate('/signup');
                  }}
                  underline>
                  Sign Up
                </Link>
              </Text>
            </Row>
          </Form>

          {!isMobile && <FooterLinks.DownloadApps />}

          <Divider />

          <div className="mb-5">
            <Paragraph type="secondary" className="text-center">
              Copyrights &copy; {new Date().getFullYear()} Buyofuel - All rights reserved
            </Paragraph>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default Login;
